import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  styled,
} from '@mui/material';

import { CurrencyCode } from 'further-types/lp/vehicle';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { type ApiResponses } from 'lib/trpcClient';
import { round } from 'lodash';
import React, { useState } from 'react';

type BaseAllocation =
  ApiResponses['capitalAction']['getCommitmentsWithCapitalContributionBalances'][number];

const StyledAccordion = styled(Accordion)({
  width: '100%',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  ...theme.typography.h6,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const AccordionBody = styled(AccordionDetails)({
  padding: 0,
  border: 'none',
});

type Props = {
  title: string;
  calledFundsLabel: string;
  currency: CurrencyCode;
  baseAllocations: Array<BaseAllocation>;
  onChange: (
    capitalBreakdown: Array<{
      commitmentId: string;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => void;
  capitalBreakdown: Array<{
    commitmentId: string;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
};

const CapitalContributionCallAllocations: React.FC<Props> = ({
  title,
  currency,
  baseAllocations,
  onChange,
  capitalBreakdown,
  calledFundsLabel,
}) => {
  const [excludedCommitmentIds, setExcludedCommitmentIds] = useState<
    Array<string>
  >(
    capitalBreakdown
      .filter(
        (cb) =>
          round(
            (cb.fundsCalled?.fromCommitmentLcy ?? 0) +
              (cb.fundsCalled?.fromRecallableDistributionLcy ?? 0),
            2,
          ) === 0,
      )
      .map((c) => c.commitmentId),
  );

  const capitalContributionAllocations = baseAllocations.map((allocation) => {
    const isExcluded = excludedCommitmentIds.includes(allocation.commitmentId);
    return {
      ...allocation,
      excludeFromAllocation: isExcluded,
      capitalContributionCalledLcy: isExcluded
        ? 0
        : allocation.fundsCalled.fromCommitmentLcy,
      fundsCalled: {
        fromCommitmentLcy: isExcluded
          ? 0
          : allocation.fundsCalled.fromCommitmentLcy,
        fromRecallableDistributionLcy: 0,
      },
    };
  });

  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Total capital contribution',
      key: 'totalCommitmentLcy',
    },
    {
      label: 'Uncalled capital contribution',
      key: 'unallocatedCommitmentLcy',
    },
    {
      label: 'Close date',
      key: 'closeDate',
    },
    {
      label: calledFundsLabel,
      key: 'capitalContributionCalledLcy',
    },
    {
      label: 'Exclude from allocation',
      key: 'excludeFromAllocation',
      render: (row: {
        commitmentId: string;
        excludeFromAllocation: boolean;
        unallocatedCommitmentRaw: number;
      }) => (
        <Checkbox
          disabled={row.unallocatedCommitmentRaw === 0}
          checked={
            row.excludeFromAllocation || row.unallocatedCommitmentRaw === 0
          }
          onChange={(e) => {
            const newExcludedIds = e.target.checked
              ? [...excludedCommitmentIds, row.commitmentId]
              : excludedCommitmentIds.filter((id) => id !== row.commitmentId);
            setExcludedCommitmentIds(newExcludedIds);
            onChange(
              baseAllocations.map((a) => {
                const isExcluded = newExcludedIds.includes(a.commitmentId);
                return {
                  commitmentId: a.commitmentId,
                  fundsCalled: {
                    fromCommitmentLcy: isExcluded
                      ? 0
                      : a.fundsCalled.fromCommitmentLcy,
                    fromRecallableDistributionLcy: 0,
                  },
                };
              }),
            );
          }}
        />
      ),
    },
  ];

  const rows = capitalContributionAllocations.map((allocation) => ({
    id: allocation.commitmentId,
    commitmentId: allocation.commitmentId,
    investorLabel: allocation.investorLabel,
    totalCommitmentLcy: numberToCurrencyString(allocation.totalCommitmentLcy, {
      currency,
    }),
    unallocatedCommitmentLcy: numberToCurrencyString(
      allocation.unallocatedCommitmentLcy,
      { currency },
    ),
    capitalContributionCalledLcy: numberToCurrencyString(
      allocation.capitalContributionCalledLcy,
      { currency },
    ),
    closeDate: allocation.closeDate.toLabel(),
    excludeFromAllocation: allocation.excludeFromAllocation,
    unallocatedCommitmentRaw: allocation.unallocatedCommitmentLcy,
  }));

  return (
    <StyledAccordion defaultExpanded>
      <AccordionHeader expandIcon={<ExpandMoreIcon />}>{title}</AccordionHeader>
      <AccordionBody>
        <Table showHeaderBorder rows={rows} columns={columns} />
      </AccordionBody>
    </StyledAccordion>
  );
};

export default CapitalContributionCallAllocations;
